import type { Mindmap, NodeComment, NodeOut, NodePatch, TreeNode } from "./types";

class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`API ${status}: ${JSON.stringify(body)}`);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(extraHeaders ?? {}),
  };
  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let parsed: unknown = null;
    try {
      parsed = await res.json();
    } catch {
      parsed = await res.text();
    }
    throw new ApiError(res.status, parsed);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  getMindmap: (id: string) => request<Mindmap>("GET", `/v1/mindmaps/${id}`),
  getTree: (id: string) =>
    request<{ mindmap: Mindmap; tree: TreeNode | null }>("GET", `/v1/mindmaps/${id}/tree`),
  createNode: (
    mindmapId: string,
    parent_id: string,
    title: string,
    options: { after_sibling_id?: string; before_sibling_id?: string } = {},
  ) =>
    request<NodeOut>("POST", `/v1/mindmaps/${mindmapId}/nodes`, {
      parent_id,
      title,
      ...options,
    }),
  patchNode: (id: string, version: number, body: NodePatch) =>
    request<NodeOut>("PATCH", `/v1/nodes/${id}`, body, { "If-Match": String(version) }),
  moveNode: (
    id: string,
    version: number,
    new_parent_id: string,
    options: { after_sibling_id?: string; before_sibling_id?: string } = {},
  ) =>
    request<NodeOut>(
      "POST",
      `/v1/nodes/${id}/move`,
      { new_parent_id, ...options },
      { "If-Match": String(version) },
    ),
  deleteNode: (id: string, version: number) =>
    request<void>("DELETE", `/v1/nodes/${id}`, undefined, { "If-Match": String(version) }),
  listNodeComments: (nodeId: string) =>
    request<NodeComment[]>("GET", `/v1/nodes/${nodeId}/comments`),
  createNodeComment: (nodeId: string, body: string) =>
    request<NodeComment>("POST", `/v1/nodes/${nodeId}/comments`, { body }),
  deleteNodeComment: (nodeId: string, commentId: number) =>
    request<void>("DELETE", `/v1/nodes/${nodeId}/comments/${commentId}`),
};

export { ApiError };
